import { ApiError } from '../lib/api';
import type { Freshness } from '../types/Freshness';
import type { JourneyJson } from '../types/JourneyJson';
import { SupersedableStore } from './supersedable-store.svelte';

/** Lifecycle of the last route query. */
export type RouteState = 'idle' | 'loading' | 'ready' | 'error';

/** One route response as the store consumes it. */
export interface RouteResponsePayload {
  journeys: JourneyJson[];
  realtime: Freshness;
}

/** Just the network machinery a caller can inject in tests. */
export interface RouteLoaderDependencies {
  /** Injectable timeout for deterministic tests; production uses {@link LOAD_TIMEOUT_MS}. */
  timeoutMs?: number;
}

/** Generic-error message shown when a fetch fails with a non-{@link ApiError} exception. */
const GENERIC_ERROR = 'Search failed. Try again.';
/** Message shown when the network is unreachable (native `fetch` `TypeError`). */
const NETWORK_ERROR = 'Network unavailable. Check your connection and try again.';
/** Message shown when the fetch exceeds {@link LOAD_TIMEOUT_MS}. */
const TIMEOUT_ERROR = 'Search timed out.';
/** How long to wait for the /v1/routes response before giving up. */
const LOAD_TIMEOUT_MS = 15_000;

/**
 * Reactive holder for the last `/v1/routes` response + which journey the user
 * has expanded inline. `selectedIndex = -1` means every card is collapsed.
 * Instantiate a fresh copy per test to isolate state.
 */
export class RouteResponseStore extends SupersedableStore<RouteResponsePayload> {
  #dependencies: RouteLoaderDependencies;
  state = $state<RouteState>('idle');
  journeys = $state<JourneyJson[]>([]);
  freshness = $state<Freshness | undefined>(undefined);
  errorMessage = $state<string | undefined>(undefined);
  selectedIndex = $state(-1);

  constructor(dependencies: RouteLoaderDependencies = {}) {
    super();
    this.#dependencies = dependencies;
  }

  /** Enters `loading`; keeps existing journeys visible for smoother transitions. */
  setLoading(): void {
    this.state = 'loading';
    this.errorMessage = undefined;
  }

  /**
   * Enters `ready` with a fresh result set and collapses every card
   * (`selectedIndex = -1`). A non-array `journeys` transitions to `error`
   * instead of crashing render.
   */
  setSuccess(journeys: JourneyJson[], freshness: Freshness): void {
    if (!Array.isArray(journeys)) {
      this.setError('Malformed route response.');
      return;
    }
    this.state = 'ready';
    this.journeys = journeys;
    this.freshness = freshness;
    this.errorMessage = undefined;
    this.selectedIndex = -1;
  }

  /** Enters `error` with a user-visible message. */
  setError(message: string): void {
    this.state = 'error';
    this.errorMessage = message;
  }

  /**
   * Selects `index` (or collapses if it's already selected). Ignores
   * out-of-range indices — the current selection is preserved.
   */
  select(index: number): void {
    if (index < 0 || index >= this.journeys.length) return;
    this.selectedIndex = this.selectedIndex === index ? -1 : index;
  }

  /** The selected journey, or undefined when nothing is selected or the index is stale. */
  get selectedJourney(): JourneyJson | undefined {
    const index = this.selectedIndex;
    if (index < 0) return undefined;
    if (index >= this.journeys.length) return undefined;
    return this.journeys[index];
  }

  /**
   * Runs `fetcher` with a fresh {@link AbortSignal}, superseding any in-flight
   * request. Resolves `true` on `ready`; `false` when aborted, superseded, or
   * failed. Timeouts and non-abort errors set state to `error` only when the
   * request is still current — a stale rejection leaves state to the newer caller.
   */
  async load(fetcher: (signal: AbortSignal) => Promise<RouteResponsePayload>): Promise<boolean> {
    this.setLoading();
    return this.runWithSupersede(fetcher, this.#dependencies.timeoutMs ?? LOAD_TIMEOUT_MS);
  }

  protected override applyResponse(response: RouteResponsePayload): void {
    this.setSuccess(response.journeys, response.realtime);
  }

  protected override classifyError(error: unknown): string {
    if (error instanceof ApiError) return error.message;
    if (error instanceof TypeError) return NETWORK_ERROR;
    return GENERIC_ERROR;
  }

  protected override timeoutMessage(): string {
    return TIMEOUT_ERROR;
  }
}
